import * as React from 'react';
import { cn } from '@/lib/utils';
import { Field } from './field';

/**
 * Tela "Meu perfil" — ver prints/04-perfil.png e prints/05-perfil-senha.png.
 * Ordem: ProfileHero → grid auto-fit 340px com dois SectionCard (01 Dados pessoais, 02 Segurança) → SaveBar fixa.
 */

/** Faixa de identidade: capa 136px com a foto do site escurecida, avatar 116px com anel areia sobreposto (-58px). */
export const ProfileHero: React.FC<{
  name: string;
  email: string;
  avatarUrl?: string;
  role: string;
  active?: boolean;
  onChangePhoto?: () => void;
  /** Até 3 itens. O último pode vir com tone="sand" (ex: "Há 94 dias"). */
  meta: { label: string; value: string; tone?: 'sand' }[];
  cameraIcon: React.ReactNode;
  shieldIcon: React.ReactNode;
  mailIcon: React.ReactNode;
}> = ({
  name,
  email,
  avatarUrl,
  role,
  active = true,
  onChangePhoto,
  meta,
  cameraIcon,
  shieldIcon,
  mailIcon,
}) => (
  <section className="relative overflow-hidden rounded-[18px] border border-border bg-card">
    <div className="relative h-[110px] border-b border-border-subtle bg-sunken">
      <span className="col-label absolute right-[22px] top-[18px] flex items-center gap-2 tracking-[.18em] text-faint">
        <span className="h-px w-[18px] bg-faint" />
        Meu perfil
      </span>
    </div>

    <div className="relative -mt-[58px] flex flex-wrap items-end gap-[22px] px-7 pb-[26px]">
      <div className="relative shrink-0">
        <span className="block size-[116px] rounded-full bg-[linear-gradient(145deg,var(--sand),color-mix(in_oklch,var(--sand)_15%,transparent)_55%,var(--olive-line))] p-[3px]">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="size-full rounded-full border-[3px] border-card object-cover"
            />
          ) : (
            <span className="font-display flex size-full items-center justify-center rounded-full border-[3px] border-card bg-accent text-3xl font-extrabold text-olive-ink">
              {name.slice(0, 2).toUpperCase()}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={onChangePhoto}
          aria-label="Alterar foto"
          className="absolute bottom-1 right-0.5 flex size-9 items-center justify-center rounded-full border border-border bg-raised text-sand transition-colors hover:border-sand/40"
        >
          {cameraIcon}
        </button>
      </div>

      <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-2.5 pb-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="col-label flex h-6 items-center gap-1.5 rounded-full border border-sand/35 px-2.5 text-sand">
            {shieldIcon}
            {role}
          </span>
          {active && (
            <span className="col-label flex h-6 items-center gap-[7px] rounded-full border border-olive-line/40 bg-accent px-2.5 text-olive-ink">
              <span className="size-1.5 rounded-full bg-olive-icon shadow-[0_0_0_3px_color-mix(in_oklch,var(--olive-icon)_16%,transparent)]" />
              Conta ativa
            </span>
          )}
        </span>
        <span className="font-display truncate text-[clamp(26px,2.6vw,34px)] font-extrabold uppercase leading-none tracking-[-.03em] text-white">
          {name}
        </span>
        <span className="flex items-center gap-2 text-[13.5px] text-muted-foreground">
          <span className="text-faint">{mailIcon}</span>
          {email}
        </span>
      </div>

      <div className="grid shrink grid-cols-[repeat(3,auto)] rounded-[14px] border border-border bg-sunken">
        {meta.map((m, i) => (
          <span
            key={m.label}
            className={cn(
              'flex flex-col gap-1.5 px-5 py-3.5',
              i > 0 && 'border-l border-border',
            )}
          >
            <span className="col-label text-faint">{m.label}</span>
            <span
              className={cn(
                'font-display text-[15px] font-extrabold tracking-[-.01em]',
                m.tone === 'sand' ? 'text-sand' : 'text-white',
              )}
            >
              {m.value}
            </span>
          </span>
        ))}
      </div>
    </div>
  </section>
);

/** Card de seção numerada: "01" em Barlow faint à esquerda do título. Corpo empilhado (flex col, gap 16). */
export const SectionCard: React.FC<{
  index: string;
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ index, title, description, children }) => (
  <section className="overflow-hidden rounded-2xl border border-border bg-card">
    <div className="flex items-start gap-4 border-b border-border px-[26px] py-[22px]">
      <span className="font-display pt-[3px] text-[13px] font-extrabold text-faint">
        {index}
      </span>
      <span className="flex min-w-0 flex-col gap-1.5">
        <span className="font-display text-[18px] font-extrabold uppercase tracking-tight text-white">
          {title}
        </span>
        <span className="text-[13px] text-muted-foreground">{description}</span>
      </span>
    </div>
    <div className="flex flex-col gap-4 p-[26px]">{children}</div>
  </section>
);

/** Campo somente leitura: mesma geometria do Field, borda TRACEJADA, fundo transparente, valor em muted. */
export const ReadonlyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex min-w-0 flex-col gap-1.5 rounded-[14px] border border-dashed border-border px-[18px] py-[13px]">
    <span className="col-label flex items-center gap-2 text-muted-foreground">
      {label}
      <span className="ml-auto tracking-[.1em] text-faint">Somente leitura</span>
    </span>
    <span className="truncate text-base font-semibold text-muted-foreground">
      {value}
    </span>
  </div>
);

/** Aviso oliva dentro do card (ícone info + texto 12.5px). */
export const InlineNote: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <span className="flex items-start gap-2.5 rounded-xl border border-olive-line/40 bg-accent px-4 py-3.5 text-[12.5px] leading-normal text-muted-foreground">
    <span className="shrink-0 pt-px text-olive-icon">{icon}</span>
    <span>{children}</span>
  </span>
);

/** Field de senha com "Mostrar/Ocultar" em texto (col-label faint → sand no hover). `invalid` pinta a borda de vermelho. */
export const PasswordField: React.FC<
  Omit<React.ComponentProps<typeof Field>, 'type' | 'trailing'> & { invalid?: boolean }
> = ({ invalid, fieldClassName, ...props }) => {
  const [show, setShow] = React.useState(false);
  return (
    <Field
      autoComplete="new-password"
      {...props}
      type={show ? 'text' : 'password'}
      className="tracking-[.02em]"
      fieldClassName={cn(invalid && 'border-destructive/60', fieldClassName)}
      trailing={
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="col-label shrink-0 text-faint transition-colors hover:text-sand"
        >
          {show ? 'Ocultar' : 'Mostrar'}
        </button>
      }
    />
  );
};

/**
 * Regras exibidas no checklist — espelham o `passwordSchema` de `src/api/auth/auth.schema.ts`
 * (mín. 8, uma minúscula, um número) + confirmação. A barra de força conta exatamente essas regras.
 */
export const passwordRules = (pw: string, confirm: string) => [
  { label: 'Mínimo de 8 caracteres', ok: pw.length >= 8 },
  { label: 'Uma letra minúscula', ok: /[a-z]/.test(pw) },
  { label: 'Um número', ok: /[0-9]/.test(pw) },
  { label: 'Senhas coincidem', ok: pw.length > 0 && pw === confirm },
];

/** Medidor de força: 4 segmentos de 4px (um por regra) + checklist. Fica em bg-sunken logo abaixo dos campos. */
export const PasswordStrength: React.FC<{
  password: string;
  confirm: string;
  checkIcon: React.ReactNode;
}> = ({ password, confirm, checkIcon }) => {
  const rules = passwordRules(password, confirm);
  const score = rules.filter((r) => r.ok).length;
  const tone =
    score <= 1 ? 'bg-destructive' : score <= 3 ? 'bg-sand-dim' : 'bg-olive-icon';
  const toneText = !password
    ? 'text-faint'
    : score <= 1
      ? 'text-destructive'
      : score <= 3
        ? 'text-sand-dim'
        : 'text-olive-icon';
  const label = !password
    ? 'Aguardando'
    : ['Muito fraca', 'Fraca', 'Média', 'Quase lá', 'Válida'][score];
  return (
    <div className="flex flex-col gap-3.5 rounded-[14px] border border-border bg-sunken px-[18px] py-4">
      <span className="flex items-center gap-3">
        <span className="col-label text-faint">Força da senha</span>
        <span className={cn('col-label ml-auto', toneText)}>{label}</span>
      </span>
      <span className="grid grid-cols-4 gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={cn(
              'h-1 rounded-full transition-colors duration-200',
              i < score ? tone : 'bg-sand/10',
            )}
          />
        ))}
      </span>
      <span className="flex flex-col gap-2.5">
        {rules.map((r) => (
          <span
            key={r.label}
            className={cn(
              'flex items-center gap-[9px] text-[12.5px] transition-colors',
              r.ok ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            <span
              className={cn(
                'flex size-[18px] shrink-0 items-center justify-center rounded-full border text-olive-ink transition-all',
                r.ok ? 'border-olive-line bg-accent' : 'border-border',
              )}
            >
              {r.ok && checkIcon}
            </span>
            {r.label}
          </span>
        ))}
      </span>
    </div>
  );
};

/**
 * Barra de salvar fixa no rodapé (left = largura do sidebar, --sidebar-width). Blur 20px sobre bg-background/90.
 * Status à esquerda: "Tudo atualizado" (dot oliva) · "Alterações não salvas" (areia) · erro (vermelho) · "Alterações salvas".
 * O conteúdo da página precisa de pb-24 para não ficar por baixo.
 */
export const SaveBar: React.FC<{
  status: 'clean' | 'dirty' | 'error' | 'saved';
  message?: string;
  onDiscard: () => void;
  onSave: () => void;
  saveIcon: React.ReactNode;
  saving?: boolean;
}> = ({ status, message, onDiscard, onSave, saveIcon, saving }) => {
  const text =
    message ??
    {
      clean: 'Tudo atualizado',
      dirty: 'Alterações não salvas',
      error: 'Verifique os campos',
      saved: 'Alterações salvas',
    }[status];
  const canSave = status === 'dirty' && !saving;
  return (
    <div className="fixed inset-x-0 bottom-0 z-25 border-t border-border bg-background/90 px-[clamp(20px,2.4vw,32px)] py-3.5 backdrop-blur-[20px] md:left-(--sidebar-width)">
      <div className="flex flex-wrap items-center gap-2.5">
        <span
          className={cn(
            'mr-auto flex items-center gap-2.5 text-[12.5px]',
            status === 'error'
              ? 'text-danger-soft'
              : status === 'dirty'
                ? 'text-sand'
                : 'text-muted-foreground',
          )}
        >
          <span
            className={cn(
              'size-[7px] rounded-full',
              status === 'error'
                ? 'bg-destructive'
                : status === 'dirty'
                  ? 'bg-sand'
                  : 'bg-olive-icon',
            )}
          />
          {text}
        </span>
        <button
          type="button"
          onClick={onDiscard}
          className="flex h-11 items-center rounded-full border border-border px-[22px] text-[13.5px] font-semibold text-muted-foreground transition-colors hover:text-white"
        >
          Descartar
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave}
          className="flex h-11 items-center gap-2 rounded-full bg-sand px-6 text-[13.5px] font-bold text-on-sand transition-colors hover:bg-sand/90 disabled:opacity-40"
        >
          {saveIcon}
          Salvar alterações
        </button>
      </div>
    </div>
  );
};
